import React, { useEffect, useMemo, useState } from "react";
import { interesesCortos } from "../../../utils/cursoCorto";
import { primerCurso } from "./cursosSelect";

import HttpManager from "../../../services/HttpManager";
import { ScheduleCatalogService } from "../../../services/v2/scheduleCatalog.service";
import { Articulo, ProspectCatalogs, ProspectDetails } from "../../../services/v2/prospects.service";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { cuando, dinero, errorTexto, fechaDia, GESTION_RESULTADOS, nombreCompleto, plano, SIMBOLO_PLANTEL } from "./salesHelpers";

interface Props {
	campusId: number;
	det: ProspectDetails;
	onEditar: () => void;
	onEnviarAlChat: (texto: string) => void;
	onPaso: (paso: "promesa" | "llamar" | "no_interesa") => void;
	onAbrir: (id: number) => void;
	onQuitarVinculo: (linkId: number) => void;
	onPromo: () => void;
	onAgendar: (motivo: string) => void;
	catalogs: ProspectCatalogs | null;
	/** Corre una acción de la ficha (guarda, recarga la ficha y la conversación). */
	onAccion: (fn: () => Promise<any>) => Promise<void>;
	/** CRM: "Copiar para Woztell" (el chat sigue en Woztell); WappChat: "Enviar al chat". */
	textoAlChat?: string;
	/**
	 * Ficha del CRM (Esteban, 2026-10-06): solo lo que ES del cliente (su matrícula, kit, cuando estudió, gestiones).
	 * Horarios, cotización, siguiente paso, vínculos y "venía de Zoho" se quedan en el chat: en la ficha parecía
	 * que ya estaba inscrito en ese horario.
	 */
	soloFicha?: boolean;
	/** La ficha del CRM pone "Equipo del Cliente" aparte, arriba de Información. */
	sinEquipo?: boolean;
}

const catalogo = new ScheduleCatalogService();
const cache: { [campus: number]: Promise<any[]> } = {};
const horariosDe = (campusId: number): Promise<any[]> => {
	if (!cache[campusId]) {
		cache[campusId] = catalogo.catalogue(campusId).then((r) => r.data?.horarios || []).catch(() => { delete cache[campusId]; return []; });
	}
	return cache[campusId];
};

/** Palabras del curso que dijo ("Pasteleria / Reposteria") para empatar con el catálogo de horarios. */
const palabras = (s: string | null | undefined): string[] =>
	plano(s).split(/[^a-zñ]+/).filter((w) => w.length >= 4 && !["curso", "cursos", "para", "info", "informacion", "clases", "diplomado"].includes(w));

const cortoCurso = (s: string) => s.replace(/^Diplomado de /i, "");

/**
 * Columna derecha del lienzo. Cambia con la Etapa: Pre-Inscripción (qué le interesa, horarios, cotización),
 * Matriculado (su matrícula, kit, qué ofrecerle hoy), Retirado (cuando estudió, qué ofrecerle hoy). Todo lo de
 * alumno viene de WAPP (det.wapp).
 */
const LadoCliente: React.FC<Props> = ({ campusId, det, onEditar, onEnviarAlChat, onPaso, onAbrir, onQuitarVinculo, onPromo, onAgendar, catalogs, onAccion, textoAlChat, soloFicha, sinEquipo }) => {
	const http = HttpManager.getInstance();
	const p = det.prospect;
	const w = det.wapp;
	const plantel = p.campus_id || campusId;
	const sim = SIMBOLO_PLANTEL[plantel] || "";
	const [horarios, setHorarios] = useState<any[] | null>(null);
	const [marcados, setMarcados] = useState<number[]>([]);
	// Vitrina: productos de la tienda del plantel para ofrecer por el chat.
	const [vitrina, setVitrina] = useState<Articulo[] | null>(null);
	const [vBusca, setVBusca] = useState<string>("");
	const [vError, setVError] = useState<string | null>(null);
	const buscarVitrina = (q: string) => {
		setVError(null);
		http.prospectsService.vitrina(campusId, q).then((r) => setVitrina(r.data)).catch((e) => { setVitrina([]); setVError(errorTexto(e)); });
	};
	useEffect(() => { setVitrina(null); setVBusca(""); }, [p.id]);

	useEffect(() => {
		setMarcados([]); setHorarios(null);
		horariosDe(campusId).then(setHorarios);
	}, [campusId, p.id]);

	// Curso que le interesa (o el que dejó): para filtrar los horarios abiertos. La cotización se quitó de WappChat
	// (Esteban, 14-oct-2026: imprecisa).
	const cursoCotizar = p.etapa_id === 3 && w?.retiro ? w.retiro.curso : primerCurso(p.course_interest_text);

	const abiertos = useMemo(() => {
		if (!horarios) return [];
		const ws = palabras(cursoCotizar);
		if (!ws.length) return [];
		return horarios.filter((h) => h.plantel === plantel && h.estado === "por-comenzar"
			&& ws.some((wd) => plano(h.curso + " " + h.modulo).includes(wd.slice(0, 6))))
			.sort((a, b) => String(a.inicio).localeCompare(String(b.inicio))).slice(0, 8);
	}, [horarios, cursoCotizar, plantel]);

	const enviarHorarios = () => {
		const txt = abiertos.filter((h) => marcados.includes(h.id)).map((h) =>
			`*Diplomado:* ${h.curso}\n*Periodo:* ${h.periodo}\n*Horario:* ${h.horario}\nInicia: ${h.inicioTexto}`).join("\n\n");
		if (txt) onEnviarAlChat(txt);
	};
	const accionOferta = (o: { kind: string; title: string; detail: string }) => {
		if (o.kind === "promo") return onPromo();
		if (o.kind === "kit") return onEnviarAlChat(`Hola ${(p.first_name || "").split(" ")[0]}! Le recuerdo que su kit quedó con saldo de ${o.detail.replace(" pendientes", "")}. Lo puede abonar en caja o por transferencia 😊`);
		if (o.kind === "retencion") return onAgendar("Retención: " + o.title);
		if (o.kind === "reingreso" && o.title.startsWith("Retoma")) return onEnviarAlChat(`Te puedo reactivar justo donde te quedaste, en ${o.detail.split(" · ")[0]}: no repites nada de lo que ya llevabas. Y la inscripción va gratis si vuelves esta semana.`);
		if (o.kind === "mas_cursos") return document.getElementById("lado-horarios")?.scrollIntoView({ behavior: "smooth" });
		return onAgendar(o.title);
	};

	const act = w?.active || null;
	const kits = w?.kits || [];
	const kitPendiente = kits.find((k) => k.estado === "Abono sin liquidar" || k.estado === "Cargado sin pago");

	return (
		<div className="cv-lado">
			{/* Equipo del cliente arriba de todo (Esteban, 2026-10-13: "debería aparecer más arriba"). */}
			{sinEquipo ? null : <EquipoCliente campusId={campusId} det={det} catalogs={catalogs} onAccion={onAccion} />}
			{/* Pre-Inscripción / sin alumno: qué le interesa */}
			{!soloFicha && !act && !w?.retiro ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Qué le interesa</h4><button className="enlace" onClick={onEditar}>Editar</button></div>
					<div className="par"><span>Curso</span><span>{interesesCortos(p.course_interest_text).join(", ") || "—"}</span></div>
					<div className="par"><span>Plantel</span><span>{det.campuses.map((c) => c.name.replace("Washington Academy ", "")).join(", ") || "—"}</span></div>
					<div className="par"><span>Primer contacto</span><span>{cuando(p.first_contact_at)}</span></div>
					{det.source ? <div className="par"><span>Llegó por</span><span>{det.source}{p.ad_name ? " · «" + p.ad_name + "»" : ""}</span></div> : null}
					{p.phone_alt ? <div className="par"><span>Otro teléfono</span><span>{telefonoBonito(p.phone_alt)}</span></div> : null}
					{p.email ? <div className="par"><span>Correo</span><span>{p.email}</span></div> : null}
				</div>
			) : null}

			{/* Matriculado: su matrícula, kit, qué ofrecerle hoy */}
			{act ? (
				<>
					<div className="tarjeta">
						<div className="tarjeta-cab"><h4>Su matrícula</h4><button className="enlace" onClick={onEditar}>Editar datos</button></div>
						<div className="lado-linea"><b>{cortoCurso(act.curso)} · {act.modulo}</b><span className="apagado">{act.horario}{act.maestro ? " · " + act.maestro : ""}</span></div>
						<div className="par"><span>Mensualidades</span><span>{act.pagadas || 0} de {act.meses}</span></div>
						{act.proxima_fecha ? <div className="par"><span>Próxima</span><span className={act.vencidas ? "oro" : ""}>{fechaDia(act.proxima_fecha)}{act.proxima_monto ? " · " + dinero(act.proxima_monto, sim) : ""}{act.vencidas ? " · vencida" : ""}</span></div> : null}
						{act.asistencia ? <div className="par"><span>Asistencia</span><span className={act.asistencia.faltas_seguidas >= 3 ? "oro" : ""}>{act.asistencia.pct}%{act.asistencia.faltas_seguidas >= 3 ? " · " + act.asistencia.faltas_seguidas + " faltas seguidas" : ""}</span></div> : null}
						{act.end_date ? <div className="par"><span>Termina</span><span>{fechaDia(act.end_date)}{act.termina_en_dias !== null && act.termina_en_dias >= 0 && act.termina_en_dias <= 45 ? " · en " + act.termina_en_dias + " días" : ""}</span></div> : null}
						<div className="par"><span>Alumno desde</span><span>{cuando(act.created_at)}</span></div>
						{w!.enrollments.length > 1 ? <span className="apagado">{w!.enrollments.length - 1} matrícula(s) anteriores</span> : null}
					</div>
					<div className="tarjeta">
						<div className="tarjeta-cab"><h4>Kit</h4>{kitPendiente ? <span className="chip oro">{kitPendiente.estado}</span> : kits.length ? <span className="chip verde">{kits[0].estado}</span> : <span className="chip">Sin kit</span>}</div>
						{kits.slice(0, 3).map((k) => (
							<React.Fragment key={k.id}>
								<div className="par"><span>{k.kit}</span><span>{k.estado}</span></div>
								<div className="par"><span>Pagado / Total</span><span>{dinero(k.paid_amount, sim)} / {dinero(k.sub_total, sim)}</span></div>
								{k.pendiente > 0 && k.status_id !== 4 ? <div className="par"><span>Saldo por cobrar</span><span className="oro">{dinero(k.pendiente, sim)}</span></div> : null}
							</React.Fragment>
						))}
						{!kits.length ? <span className="apagado">No tiene kit cargado en WAPP.</span> : null}
					</div>
				</>
			) : null}

			{/* Retirado: cuando estudió */}
			{!act && w?.retiro ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Cuando estudió</h4><button className="enlace" onClick={onEditar}>Editar datos</button></div>
					<div className="lado-linea"><b>{cortoCurso(w.retiro.curso)} · {w.retiro.modulo}</b></div>
					<div className="par"><span>Avanzó</span><span>{w.retiro.avanzo} de {w.retiro.meses} meses</span></div>
					<div className="par"><span>Se retiró</span><span>{cuando(w.retiro.fecha)}</span></div>
					<div className="par"><span>Motivo</span><span>{w.retiro.motivo || w.retiro.ultima_nota || "sin motivo registrado"}</span></div>
					<div className="par"><span>Saldo al retirarse</span><span className={w.retiro.saldo > 0 ? "oro" : ""}>{dinero(w.retiro.saldo, sim)}</span></div>
					{w.retiro.asesora ? <div className="par"><span>Asesora de entonces</span><span>{w.retiro.asesora}</span></div> : null}
				</div>
			) : null}

			{/* Otros alumnos con su mismo número (hermano, hijo del tutor, duplicado en WAPP; Esteban, 2026-10-31). */}
			{det.alumnosLigados?.length ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>También usan este número</h4></div>
					{det.alumnosLigados.map((a) => (
						<div className="par" key={a.id}>
							<span><a href={"/students?alumno=" + a.id + (a.campus_id ? "&plantel=" + a.campus_id : "")} target="_blank" rel="noreferrer">{a.nombre}</a></span>
							<span>{a.etapa}{a.curso ? " · " + cortoCurso(a.curso) : ""}</span>
						</div>
					))}
				</div>
			) : null}

			{soloFicha ? null : <>
			{/* Vitrina (lienzo Matriculado): productos de la tienda para ofrecer por el chat. */}
			{act ? (
				<div className="tarjeta">
					<div className="tarjeta-cab"><h4>Vitrina</h4>{vitrina === null ? <button className="enlace" onClick={() => buscarVitrina("")}>Ver productos</button> : null}</div>
					{vitrina !== null ? (
						<>
							<input type="search" placeholder="Buscar producto" value={vBusca} aria-label="Buscar producto" onChange={(e) => setVBusca(e.target.value)}
								onKeyDown={(e) => { if (e.key === "Enter") buscarVitrina(vBusca.trim()); }} />
							{vError ? <span className="oro">{vError}</span> : null}
							{vitrina.slice(0, 12).map((a) => (
								<div key={a.id} className="lado-oferta">
									<div><b>{a.name}</b><br /><span className="apagado">{dinero(Number(a.base_price), sim)}</span></div>
									<button className="btn-wa" onClick={() => onEnviarAlChat(`${a.name}: ${dinero(Number(a.base_price), sim)}. Se lo aparto?`)}>Ofrecer</button>
								</div>
							))}
							{!vitrina.length && !vError ? <span className="apagado">Nada con ese nombre.</span> : null}
						</>
					) : <span className="apagado">Artículos a la venta en el plantel.</span>}
				</div>
			) : null}

			{/* Qué ofrecerle hoy (Matriculado / Retirado) */}
			{w?.ofertas?.length ? (
				<div className="tarjeta">
					<h4>Qué ofrecerle hoy</h4>
					{w.ofertas.map((o, i) => (
						<div key={i} className="lado-oferta">
							<div><b>{o.title}</b><br /><span className="apagado">{o.detail}</span></div>
							{o.action ? <button className="btn-wa" onClick={() => accionOferta(o)}>{o.action}</button> : null}
						</div>
					))}
				</div>
			) : null}

			{/* Horarios abiertos: Pre-Inscripción y Retirado */}
			{!act ? (
				<div className="tarjeta" id="lado-horarios">
					<div className="tarjeta-cab"><h4>Horarios abiertos</h4><span className="cnt">{horarios ? abiertos.length : "…"}</span></div>
					{!cursoCotizar ? <span className="apagado">Sin curso de interés: ponlo en Editar.</span>
						: horarios && !abiertos.length ? <span className="apagado">No hay horarios por comenzar de ese curso en su plantel.</span> : null}
					{abiertos.map((h) => (
						<label key={h.id} className="cv-horario">
							<input type="checkbox" checked={marcados.includes(h.id)} onChange={(e) => setMarcados(e.target.checked ? [...marcados, h.id] : marcados.filter((x) => x !== h.id))} />
							<span><strong>{h.horario}</strong><br /><span className="apagado">{h.curso}{h.modulo && h.modulo !== h.curso ? " · " + h.modulo : ""} · inicia {h.inicioTexto}{h.maestro ? " · " + h.maestro : ""}</span></span>
						</label>
					))}
					{abiertos.length ? <button className="btn-wa oscuro" disabled={!marcados.length} onClick={enviarHorarios}>{textoAlChat || "Enviar al chat"}</button> : null}
				</div>
			) : null}


			<div className="tarjeta">
				<h4>Siguiente paso</h4>
				<div className="cv-pasos">
					<button className="btn-wa" onClick={() => onPaso("promesa")}>Promesa con Cita</button>
					<button className="btn-wa" onClick={() => onPaso("llamar")}>Volver a llamar</button>
					<button className="btn-wa" onClick={() => onPaso("no_interesa")}>No le interesa</button>
				</div>
			</div>

			<div className="tarjeta">
				<h4>Vínculos</h4>
				{det.links.length ? det.links.map((l) => (
					<div key={l.id} className="par">
						<span>{l.kind.replace("_", " / ")}</span>
						<span><button className="enlace" onClick={() => onAbrir(l.prospect_id)}>{nombreCompleto(l)}</button>
							<button className="btn-wa mini" onClick={() => onQuitarVinculo(l.id)} aria-label="Quitar vínculo">×</button></span>
					</div>
				)) : <span className="apagado">Sin vínculos.</span>}
			</div>

			{det.zohoRecords.length ? (
				<details className="tarjeta">
					<summary><h4 style={{ display: "inline" }}>Venía de Zoho</h4></summary>
					{det.zohoRecords.map((r) => (
						<div key={r.zoho_id} className="par"><span>{r.zoho_module === "Leads" ? "Posible cliente" : "Contacto"}</span><span>{cuando(r.zoho_created_at)}</span></div>
					))}
				</details>
			) : null}
			</>}
		</div>
	);
};

/** Primero el área que le toca a ese objetivo (Kit → Kits, Promo → Admisiones, Académico → Académico); luego las demás. */
const AREA_DE: { [k: string]: string[] } = { kit: ["Kits", "Admisiones"], promo: ["Admisiones"], academico: ["Académico"] };
const OpcionesGestoras: React.FC<{ gente: { id: string; name: string; team: string | null }[]; objetivo?: string }> = ({ gente, objetivo }) => {
	const grupos: { [t: string]: { id: string; name: string }[] } = {};
	gente.forEach((a) => { const t = a.team || "Otros"; (grupos[t] = grupos[t] || []).push(a); });
	const primero = AREA_DE[objetivo || ""] || [];
	const orden = Object.keys(grupos).sort((x, y) => {
		const ix = primero.indexOf(x), iy = primero.indexOf(y);
		return (ix < 0 ? 99 : ix) - (iy < 0 ? 99 : iy) || x.localeCompare(y);
	});
	return <>{orden.map((t) => <optgroup key={t} label={t}>{grupos[t].map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</optgroup>)}</>;
};

/**
 * Equipo del cliente (Esteban, 2026-10-12): quién lleva cada gestión, cómo va y qué sigue. Lo ve todo el que ve al cliente;
 * el estado lo cambia su dueña o supervisión. Supervisión además se la pasa a otra persona y elige entre ventas, Kits,
 * Admisiones y Académico (2026-10-13: Lizzeth no podía poner a las de Kits).
 */
export const EquipoCliente: React.FC<{ campusId: number; det: ProspectDetails; catalogs: ProspectCatalogs | null; onAccion: (fn: () => Promise<any>) => Promise<void> }> = ({ campusId, det, catalogs, onAccion }) => {
	const http = HttpManager.getInstance();
	const p = det.prospect;
	// Gestiones: abrir (objetivo + dueña) y cerrar (cómo terminó).
	const [nuevaG, setNuevaG] = useState<boolean>(false);
	const [gObjetivo, setGObjetivo] = useState<string>("");
	const [gDuena, setGDuena] = useState<string>("");
	const [gMotivo, setGMotivo] = useState<string>("");
	const [cerrarG, setCerrarG] = useState<number | null>(null);
	const [gResultado, setGResultado] = useState<string>("logrado");
	const [gNota, setGNota] = useState<string>("");
	useEffect(() => { setNuevaG(false); setCerrarG(null); }, [p.id]);
	// Promo, Kit y Académico llevan estado propio (Equipo del cliente); las demás gestiones siguen con Cerrar.
	const conEstado = (det.equipo || []).filter((e) => e.estados.length);
	const objetivosEtapa = (catalogs?.objetivos || []).filter((o) => String(o.etapas).split(",").map(Number).includes(p.etapa_id) && o.code !== "sin_pendientes");
	// A quién se le puede dar una gestión: lo que manda la ficha (con Kits y Académico); si no viene, la rotación de ventas.
	const gente = det.gestoras && det.gestoras.length ? det.gestoras : (catalogs?.advisors || []).map((a) => ({ id: String(a.id), name: a.name, team: a.team || null }));
	const supervisa = !!catalogs?.isSupervisor && gente.length > 0;
	const abrirGestion = () => gObjetivo && onAccion(() => http.prospectsService.openGestion(campusId, p.id, { objetivo: gObjetivo, ownerZohoId: gDuena || null, reason: gMotivo || null }))
		.then(() => { setNuevaG(false); setGObjetivo(""); setGDuena(""); setGMotivo(""); });
	const cerrarGestion = (id: number) => onAccion(() => http.prospectsService.closeGestion(campusId, p.id, id, { outcome: gResultado, notes: gNota || null }))
		.then(() => { setCerrarG(null); setGNota(""); setGResultado("logrado"); });
	const pasar = (gestionId: number, zohoId: string) => zohoId && onAccion(() => http.prospectsService.gestionDuena(campusId, p.id, gestionId, zohoId));
	/** La dueña de una gestión: supervisión la cambia ahí mismo; las de Kits se pasan entre ellas la de Kit (2026-10-14); los demás solo la ven. */
	const duena = (id: number, code: string, ownerId: string | null | undefined, owner: string | null, abierta: boolean) => abierta && (supervisa || (!!det.puedePasarKit && code === "kit" && gente.length > 0)) ? (
		<select className="ec-duena-sel" value={ownerId || ""} aria-label="A cargo de" title="Pasar a otra persona" onChange={(ev) => pasar(id, ev.target.value)}>
			{!ownerId ? <option value="">Sin dueña</option> : null}
			{ownerId && !gente.some((a) => a.id === ownerId) ? <option value={ownerId}>{owner}</option> : null}
			<OpcionesGestoras gente={gente} objetivo={code} />
		</select>
	) : <span className="ec-duena">{owner || <span className="apagado">sin dueña</span>}</span>;

	if (!(p.etapa_id !== 1 || det.gestiones.length || conEstado.length)) return null;
	return (
		<div className="tarjeta ec-tarjeta">
			<div className="tarjeta-cab"><h4>Equipo del Cliente</h4>{!nuevaG && objetivosEtapa.length ? <button className="enlace" onClick={() => setNuevaG(true)}>+ Nueva</button> : null}</div>
			{p.owner_zoho_name ? (
				<div className="ec-fila">
					<span className="ec-obj">Venta</span>
					<span className="ec-duena">{p.owner_zoho_name}</span>
					<span className={"ec-estado" + (p.status_id === 9 ? " verde" : "")}>{det.status}</span>
				</div>
			) : null}
			{conEstado.map((e) => (
				<div key={"e" + e.id} className={"ec-fila" + (e.cerrada ? " cerrada" : "")}>
					<span className="ec-obj">{e.objetivo}</span>
					{duena(e.id, e.code, e.ownerId, e.owner, !e.cerrada)}
					{e.puedeCambiar ? (
						<select className={"ec-estado sel" + (e.malo ? " malo" : "")} value={e.estado || ""} aria-label={"Estado de " + e.objetivo}
							onChange={(ev) => { const v = ev.target.value; onAccion(() => http.prospectsService.gestionEstado(campusId, p.id, e.id, v)); }}>
							{e.estados.map((x) => <option key={x.code} value={x.code}>{x.name}</option>)}
						</select>
					) : <span className={"ec-estado" + (e.malo ? " malo" : "")}>{e.estadoNombre}</span>}
					{e.paso ? <span className="ec-paso">Sigue: {e.paso}{e.pasoAt ? " · " + cuando(e.pasoAt) : ""}</span> : null}
				</div>
			))}
			{det.gestiones.filter((g) => !conEstado.some((e) => e.id === g.id)).map((g) => (
				<div key={g.id} className="lado-gestion">
					<div className="par"><span><b>{g.objetivo}</b>{g.notes ? <><br /><span className="apagado">{g.notes}</span></> : null}</span>
						<span>{duena(g.id, "", g.owner_zoho_id, g.owner_name, true)}<br /><span className="apagado">{g.opened_by === "matricula" ? "al matricular" : "desde"} {cuando(g.opened_at)}</span></span></div>
					{cerrarG === g.id ? (
						<div className="lado-form">
							<select value={gResultado} onChange={(e) => setGResultado(e.target.value)} aria-label="Cómo terminó">
								{GESTION_RESULTADOS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
							</select>
							<input type="text" placeholder="Nota (opcional)" value={gNota} onChange={(e) => setGNota(e.target.value)} aria-label="Nota" />
							<div className="fila-botones">
								<button className="btn-wa pri" onClick={() => cerrarGestion(g.id)}>Cerrar gestión</button>
								<button className="btn-wa" onClick={() => setCerrarG(null)}>Cancelar</button>
							</div>
						</div>
					) : <button className="btn-wa mini" onClick={() => { setCerrarG(g.id); setGResultado("logrado"); setGNota(""); }}>Cerrar</button>}
				</div>
			))}
			{!det.gestiones.length && !conEstado.length && !nuevaG ? <span className="apagado">Sin gestiones abiertas.</span> : null}
			{nuevaG ? (
				<div className="lado-form">
					<select value={gObjetivo} onChange={(e) => setGObjetivo(e.target.value)} aria-label="Objetivo">
						<option value="">Objetivo…</option>
						{objetivosEtapa.map((o) => <option key={o.code} value={o.code}>{o.name}</option>)}
					</select>
					{supervisa ? (
						<select value={gDuena} onChange={(e) => setGDuena(e.target.value)} aria-label="A cargo de">
							<option value="">A cargo de: yo</option>
							<OpcionesGestoras gente={gente} objetivo={gObjetivo} />
						</select>
					) : null}
					<input type="text" placeholder="Para qué (opcional)" value={gMotivo} onChange={(e) => setGMotivo(e.target.value)} aria-label="Para qué" />
					<div className="fila-botones">
						<button className="btn-wa pri" disabled={!gObjetivo} onClick={abrirGestion}>Abrir gestión</button>
						<button className="btn-wa" onClick={() => setNuevaG(false)}>Cancelar</button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default LadoCliente;
